import { computed, onBeforeUnmount, ref } from 'vue'
import { useTemplateStore } from 'src/stores'
import { getItemMinSize } from 'src/stores/template-store'
import { TemplateBackgroundTypeEnum } from 'src/common/enums'

const DRAG_THRESHOLD = 3
// How close (in screen px) the moved selection's edge must get to a layout grid column edge to snap to it
const GRID_SNAP_DISTANCE = 6

function getZoomLevel() {
  const htmlElement = document.documentElement
  const zoom = Number(window.getComputedStyle(htmlElement).getPropertyValue('--zoom'))
  return zoom || 1
}

export default function useBaseTemplateMainSectionHook(props) {
  const templateStore = useTemplateStore()
  const currentDate = Date.now()

  const isLayerMovingRef = ref(false)
  const isLayerActiveRef = computed(() => templateStore.activeLayer.index === props.layerIndex)

  const backgroundStyle = computed(() =>
    templateStore.isDigitalMenu
      ? templateStore.backgroundType === TemplateBackgroundTypeEnum.COLOR
        ? `background-color: ${templateStore.background}`
        : `background: url(${typeof templateStore.background === 'string' ? `${templateStore.background}?t=${Date.now()}` : templateStore.background.blob}) center center / cover no-repeat`
      : '',
  )

  // ─── Item move (pointer-based for smooth, lag-free dragging) ────────────────
  let moveStartClientX = 0
  let moveStartClientY = 0
  let moveStartPositions = {}
  // Horizontal bounds of everything being moved, used to snap its left/right edge to the layout grid
  let moveStartBounds = null
  let moveActive = false
  let movePendingFrame = null
  let movePendingPositions = null

  function teardownMoveListeners() {
    window.removeEventListener('mousemove', onItemMove)
    window.removeEventListener('mouseup', onItemMoveEnd)
    if (movePendingFrame !== null) {
      cancelAnimationFrame(movePendingFrame)
      movePendingFrame = null
    }
  }

  function flushMove() {
    if (!movePendingPositions) {
      movePendingFrame = null
      return
    }
    templateStore.moveSelectedItemsTo(movePendingPositions)
    movePendingPositions = null
    movePendingFrame = null
  }

  function handleItemMoveStart(item, event) {
    if (event.button !== undefined && event.button !== 0) return

    templateStore.handleActiveLayer({
      index: props.layerIndex,
      id: props.layer.id,
      updatedFrom: 'section',
    })

    if (!templateStore.selectedItemIds.includes(item.id)) {
      templateStore.handleActiveItem(item)
    }

    moveStartClientX = event.clientX
    moveStartClientY = event.clientY
    moveStartPositions = {}
    moveStartBounds = null
    moveActive = false

    const idsToMove = templateStore.selectedItemIds.length > 0 ? templateStore.selectedItemIds : [item.id]
    for (const layer of templateStore.layers) {
      for (const layerItem of layer.items) {
        if (idsToMove.includes(layerItem.id)) {
          const { x, y } = layerItem.start_position
          moveStartPositions[layerItem.id] = { x, y }
          moveStartBounds = {
            left: Math.min(moveStartBounds?.left ?? x, x),
            right: Math.max(moveStartBounds?.right ?? x + layerItem.width, x + layerItem.width),
          }
        }
      }
    }

    window.addEventListener('mousemove', onItemMove)
    window.addEventListener('mouseup', onItemMoveEnd)
  }

  function onItemMove(event) {
    const rawDx = event.clientX - moveStartClientX
    const rawDy = event.clientY - moveStartClientY

    if (!moveActive) {
      if (Math.abs(rawDx) < DRAG_THRESHOLD && Math.abs(rawDy) < DRAG_THRESHOLD) return
      moveActive = true
      templateStore.isDragging = true
      // Blur any currently-focused element so text editing doesn't swallow pointer events during drag
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur()
      }
    }

    const zoomLevel = getZoomLevel()
    const dx = snapToLayoutGrid(rawDx / zoomLevel, zoomLevel)
    const dy = rawDy / zoomLevel

    const positions = {}
    for (const [id, startPos] of Object.entries(moveStartPositions)) {
      positions[Number(id)] = { x: startPos.x + dx, y: startPos.y + dy }
    }
    movePendingPositions = positions
    if (movePendingFrame === null) movePendingFrame = requestAnimationFrame(flushMove)
  }

  // While the layout grid is shown, shift dx so the moved selection's left or right edge lands on the
  // nearest column edge within GRID_SNAP_DISTANCE
  function snapToLayoutGrid(dx, zoomLevel) {
    if (!templateStore.layoutGrid.isVisible || !moveStartBounds) return dx

    const columnEdges = templateStore.layoutGridColumns.flatMap(({ left, right }) => [left, right])
    const maxOffset = GRID_SNAP_DISTANCE / zoomLevel
    let snapOffset = null
    for (const movedEdge of [moveStartBounds.left + dx, moveStartBounds.right + dx]) {
      for (const columnEdge of columnEdges) {
        const offset = columnEdge - movedEdge
        if (Math.abs(offset) <= maxOffset && (snapOffset === null || Math.abs(offset) < Math.abs(snapOffset))) {
          snapOffset = offset
        }
      }
    }
    return snapOffset === null ? dx : dx + snapOffset
  }

  // The mouseup that ends a move or resize is followed by a click on whatever is under the cursor. Swallow it:
  // it would reselect only the dragged item, toggle it out of a multi-selection, or (over the canvas
  // background) deselect everything.
  function swallowNextClick() {
    const swallow = (event) => {
      event.preventDefault()
      event.stopPropagation()
    }
    window.addEventListener('click', swallow, { capture: true, once: true })
    // The click comes right after mouseup in the same task; drop the listener if it never does
    setTimeout(() => window.removeEventListener('click', swallow, { capture: true }))
  }

  function onItemMoveEnd() {
    if (movePendingFrame !== null) {
      cancelAnimationFrame(movePendingFrame)
      movePendingFrame = null
      if (movePendingPositions) {
        templateStore.moveSelectedItemsTo(movePendingPositions)
        movePendingPositions = null
      }
    }
    const didMove = moveActive
    teardownMoveListeners()
    moveActive = false
    moveStartPositions = {}
    moveStartBounds = null
    templateStore.isDragging = false
    if (didMove) {
      templateStore._pushHistory()
      swallowNextClick()
    }
  }

  // ─── Item resize (corner handles) ───────────────────────────────────────────
  let resizeStart = null
  let resizePendingFrame = null
  let resizePendingBox = null

  function teardownResizeListeners() {
    window.removeEventListener('mousemove', onItemResizeMove)
    window.removeEventListener('mouseup', onItemResizeEnd)
    if (resizePendingFrame !== null) {
      cancelAnimationFrame(resizePendingFrame)
      resizePendingFrame = null
    }
  }

  function flushResize() {
    if (!resizePendingBox || !resizeStart) {
      resizePendingFrame = null
      return
    }
    templateStore.resizeItem(resizePendingBox)
    resizePendingBox = null
    resizePendingFrame = null
  }

  function handleItemResizeStart(item, direction, event) {
    if (event.button !== undefined && event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()

    templateStore.handleActiveLayer({
      index: props.layerIndex,
      id: props.layer.id,
      updatedFrom: 'section',
    })
    if (!templateStore.selectedItemIds.includes(item.id)) {
      templateStore.handleActiveItem(item)
    }

    // The wrapper is what rotates (around its centre), and the content's padding/border make it a bit
    // larger than width/height
    const wrapper = event.target?.closest?.('.BaseTemplateMainSection__item-wrapper')
    resizeStart = {
      id: item.id,
      direction,
      clientX: event.clientX,
      clientY: event.clientY,
      x: item.start_position.x,
      y: item.start_position.y,
      width: item.width,
      height: item.height,
      rotation: item.rotate ?? 0,
      extraWidth: Math.max(0, (wrapper?.offsetWidth ?? 0) - item.width),
      extraHeight: Math.max(0, (wrapper?.offsetHeight ?? 0) - item.height),
      minSize: getItemMinSize(item),
      isTextItem: item.type === 'text',
    }
    templateStore.isDragging = true

    window.addEventListener('mousemove', onItemResizeMove)
    window.addEventListener('mouseup', onItemResizeEnd)
  }

  // Same approach as the table map editor (TemplateMainContent): measure the drag along the item's own rotated
  // axes, and keep the corner (or side) opposite the dragged handle where it is on the canvas
  function onItemResizeMove(event) {
    if (!resizeStart) return
    const { x, y, width, height, rotation, extraWidth, extraHeight, minSize, direction } = resizeStart
    const zoomLevel = getZoomLevel()
    const dxScreen = (event.clientX - resizeStart.clientX) / zoomLevel
    const dyScreen = (event.clientY - resizeStart.clientY) / zoomLevel

    const radians = (rotation * Math.PI) / 180
    const cos = Math.cos(radians)
    const sin = Math.sin(radians)
    const dxLocal = dxScreen * cos + dyScreen * sin
    const dyLocal = -dxScreen * sin + dyScreen * cos

    // Which side of the item's own axes the dragged handle is on (0 for a side handle's other axis)
    const signX = direction.includes('e') ? 1 : direction.includes('w') ? -1 : 0
    const signY = direction.includes('s') ? 1 : direction.includes('n') ? -1 : 0

    const newWidth = Math.max(minSize.width, width + signX * dxLocal)
    // Text height follows its wrapped lines, so its handles only change the width
    const newHeight = resizeStart.isTextItem ? height : Math.max(minSize.height, height + signY * dyLocal)

    // Where the opposite corner is on the canvas, rotating around the wrapper's centre...
    const boxWidth = width + extraWidth
    const boxHeight = height + extraHeight
    const anchorLocalX = (-signX * boxWidth) / 2
    const anchorLocalY = (-signY * boxHeight) / 2
    const anchorX = x + boxWidth / 2 + anchorLocalX * cos - anchorLocalY * sin
    const anchorY = y + boxHeight / 2 + anchorLocalX * sin + anchorLocalY * cos
    // ...and the resized box placed so that corner stays there
    const newBoxWidth = newWidth + extraWidth
    const newBoxHeight = newHeight + extraHeight
    const newAnchorLocalX = (-signX * newBoxWidth) / 2
    const newAnchorLocalY = (-signY * newBoxHeight) / 2
    const newCenterX = anchorX - (newAnchorLocalX * cos - newAnchorLocalY * sin)
    const newCenterY = anchorY - (newAnchorLocalX * sin + newAnchorLocalY * cos)

    resizePendingBox = {
      id: resizeStart.id,
      x: newCenterX - newBoxWidth / 2,
      y: newCenterY - newBoxHeight / 2,
      width: newWidth,
      height: newHeight,
    }
    if (resizePendingFrame === null) resizePendingFrame = requestAnimationFrame(flushResize)
  }

  function onItemResizeEnd() {
    if (resizePendingFrame !== null) {
      cancelAnimationFrame(resizePendingFrame)
      resizePendingFrame = null
      if (resizePendingBox) {
        templateStore.resizeItem(resizePendingBox)
        resizePendingBox = null
      }
    }
    teardownResizeListeners()
    resizeStart = null
    templateStore.isDragging = false
    templateStore._pushHistory()
    // Released over the canvas background, the click would deselect the item just resized
    swallowNextClick()
  }

  // ─── Layer section resize (bottom handle) ───────────────────────────────────
  let sectionResizeStart = null
  let sectionResizePendingFrame = null
  let sectionResizePendingHeight = null

  function teardownSectionResizeListeners() {
    window.removeEventListener('mousemove', onSectionResizeMove)
    window.removeEventListener('mouseup', onSectionResizeEnd)
    if (sectionResizePendingFrame !== null) {
      cancelAnimationFrame(sectionResizePendingFrame)
      sectionResizePendingFrame = null
    }
  }

  function flushSectionResize() {
    if (sectionResizePendingHeight === null) {
      sectionResizePendingFrame = null
      return
    }
    templateStore.layers[props.layerIndex].height = sectionResizePendingHeight
    sectionResizePendingHeight = null
    sectionResizePendingFrame = null
  }

  function handleResizeStart(event) {
    if (event.button !== undefined && event.button !== 0) return
    event.preventDefault()
    sectionResizeStart = {
      clientY: event.clientY,
      height: props.layer.height,
    }
    templateStore.isDragging = true
    isLayerMovingRef.value = true

    templateStore.handleActiveLayer({
      index: props.layerIndex,
      id: props.layer.id,
      updatedFrom: 'section',
    })

    window.addEventListener('mousemove', onSectionResizeMove)
    window.addEventListener('mouseup', onSectionResizeEnd)
  }

  function onSectionResizeMove(event) {
    if (!sectionResizeStart) return
    const zoomLevel = getZoomLevel()
    const dy = (event.clientY - sectionResizeStart.clientY) / zoomLevel
    const nextHeight = Math.max(0, Math.round(sectionResizeStart.height + dy))
    sectionResizePendingHeight = nextHeight
    if (sectionResizePendingFrame === null) sectionResizePendingFrame = requestAnimationFrame(flushSectionResize)
  }

  function onSectionResizeEnd() {
    if (sectionResizePendingFrame !== null) {
      cancelAnimationFrame(sectionResizePendingFrame)
      sectionResizePendingFrame = null
      if (sectionResizePendingHeight !== null) {
        templateStore.layers[props.layerIndex].height = sectionResizePendingHeight
        sectionResizePendingHeight = null
      }
    }
    const didResize = sectionResizeStart && templateStore.layers[props.layerIndex].height !== sectionResizeStart.height
    teardownSectionResizeListeners()
    sectionResizeStart = null
    templateStore.isDragging = false
    isLayerMovingRef.value = false
    if (didResize) {
      templateStore._pushHistory()
      swallowNextClick()
    }
  }

  onBeforeUnmount(() => {
    teardownMoveListeners()
    teardownResizeListeners()
    teardownSectionResizeListeners()
  })

  return {
    templateStore,
    currentDate,
    isLayerMovingRef,
    isLayerActiveRef,
    backgroundStyle,
    handleResizeStart,
    handleItemMoveStart,
    handleItemResizeStart,
  }
}
